import DndDotsIcon from '@assets/images/icons/dnd-dots.svg?react'
import { UNIT_LABELS } from '@components/ArtBoard/Estimator/constants'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { EllipsisMenu } from '@lib/components'
import type {
  EstimateLineItem,
  EstimateUnits,
} from '@octopus-do/octopus-project-core'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import {
  changeLineItemLinked,
  deleteEstimateLineItem,
  updateEstimateLineItemRate,
  updateEstimateLineItemTitle,
  updateEstimateLineItemValue,
} from '@store/reducers/estimates/actions'
import type { AppState } from '@store/types'
import cx from 'classnames'
import type React from 'react'
import { type CSSProperties, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import type { Action } from 'redux'
import type { ThunkDispatch } from 'redux-thunk'

import { GtmTriggerName, sendGtmEvent } from '@/constants/gtm'
import { toFloat } from '@/helpers'
import { useArtBoardDisabled } from '@/hooks/selectors/useArtBoardDisabled'
import { Input } from '../Input'
import { SelectedButton } from '../SelectedButton'
import type { SelectedCell } from './Table'
import css from './Table.module.scss'

type Props = {
  item: EstimateLineItem
  unitKey: EstimateUnits
  selectedCell: SelectedCell | null
  onSelectCell: (obj: SelectedCell | null) => void
}

export enum InputType {
  NAME = 'name',
  RATE = 'rate',
  VALUE = 'value',
}

export const TableItem: React.FC<Props> = ({
  item,
  unitKey,
  selectedCell,
  onSelectCell,
}) => {
  const dispatch = useDispatch<ThunkDispatch<AppState, unknown, Action>>()

  const { id, title, color, rate, value, linked } = item

  const estimateHours = useSelector(
    migrationProjectSelector.getProjectNodeEstimatesHoursByLineItemId(id)
  )

  const amount = linked ? rate * estimateHours : rate * value
  const amountValue = amount.toFixed(2)

  const hours = linked ? estimateHours : value

  // The artboard is off: the cells read as for a viewer, the handle and
  // Remove stay in place and do nothing.
  const artboardDisabled = useArtBoardDisabled()

  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id, disabled: artboardDisabled })

  const isReadOnly = useSelector(
    migrationProjectSelector.getProjectLegacyAccessTypeIsReadOnly
  )

  const locked = isReadOnly || artboardDisabled

  const style: CSSProperties = useMemo(() => {
    return {
      transform: CSS.Transform.toString(transform),
      transition,
    }
  }, [transform, transition])

  const handleToggleItem = () => {
    dispatch(changeLineItemLinked({ id, linked: !linked }))
  }

  const handleSelectCell = (key: string) => {
    onSelectCell({
      [key]: id,
    })
  }

  const handleUpdateItem = (value: string | number, type: InputType) => {
    if (type === InputType.NAME) {
      dispatch(updateEstimateLineItemTitle({ id, title: String(value) }))

      return
    }

    if (type === InputType.RATE) {
      dispatch(updateEstimateLineItemRate({ id, rate: Number(value) }))

      return
    }

    if (type === InputType.VALUE) {
      sendGtmEvent(
        GtmTriggerName.ESTIM_HOUR_MANUAL,
        'Add hours in estimate table'
      )

      dispatch(updateEstimateLineItemValue({ id, value: toFloat(value) }))
    }
  }

  const onDelete = (id: string) => {
    dispatch(deleteEstimateLineItem(id))
  }

  const resetSelectedCell = () => {
    onSelectCell(null)
  }

  const renderRate = String(rate).length < 7
  const renderUnitKey = String(hours).length >= 7 ? null : unitKey
  const renderAmount = String(amount).length < 8

  return (
    <div className={css.tableRow} ref={setNodeRef} style={style}>
      <div
        className={cx(css.tableCell, {
          [css.selected]: selectedCell?.name === id,
          [css.readOnly]: isReadOnly,
        })}
      >
        {!isReadOnly && (
          <div className={css.dndArea} {...attributes} {...listeners}>
            <DndDotsIcon />
          </div>
        )}

        <SelectedButton
          unit={UNIT_LABELS[unitKey]}
          isEstimate={linked}
          color={color}
          onToggleItem={handleToggleItem}
        />

        <div className={css.nameWrapper}>
          {selectedCell?.name === id && !locked ? (
            <div className={css.inputWrapper}>
              <Input
                name={InputType.NAME}
                defaultValue={title}
                width={136}
                onChange={(value) => handleUpdateItem(value, InputType.NAME)}
                onResetSelectedCall={resetSelectedCell}
              />
            </div>
          ) : (
            <div
              className={cx(css.name, {
                [css.disabled]: locked,
              })}
              onClick={() => {
                if (!locked) {
                  handleSelectCell('name')
                }
              }}
            >
              <span>{title}</span>
            </div>
          )}

          {!isReadOnly && (
            <EllipsisMenu customClassName={css.ellipsisBtn}>
              <button
                className={css.removeBtn}
                disabled={artboardDisabled}
                onClick={() => onDelete(id)}
              >
                Remove
              </button>
            </EllipsisMenu>
          )}
        </div>
      </div>

      <div
        className={cx(css.tableCell, {
          [css.selected]: selectedCell?.rate === id,
          [css.pointer]: selectedCell?.rate !== id && !locked,
          [css.flexStart]: !renderRate,
          [css.disabled]: locked,
        })}
        onClick={() => {
          if (!locked) {
            handleSelectCell('rate')
          }
        }}
      >
        {selectedCell?.rate === id ? (
          <div className={css.inputWrapper}>
            <Input
              name={InputType.RATE}
              defaultValue={rate}
              textAlign="right"
              onChange={(value) => handleUpdateItem(value, InputType.RATE)}
              onResetSelectedCall={resetSelectedCell}
            />
          </div>
        ) : (
          rate
        )}
      </div>

      <div
        className={cx(css.tableCell, {
          [css.selected]: selectedCell?.unit === id,
          [css.pointer]: selectedCell?.rate !== id && !linked && !locked,
          [css.flexStart]: !renderUnitKey,
          [css.disabled]: linked || locked,
        })}
        onClick={() => {
          if (!linked && !locked) {
            handleSelectCell(InputType.VALUE)
          }
        }}
      >
        {selectedCell?.value === id ? (
          <div className={css.inputWrapper}>
            <Input
              name={InputType.VALUE}
              defaultValue={hours ?? ''}
              textAlign="right"
              onChange={(value) => handleUpdateItem(value, InputType.VALUE)}
              onResetSelectedCall={resetSelectedCell}
            />
          </div>
        ) : (
          <>
            {hours}
            {renderUnitKey}
          </>
        )}
      </div>
      <div
        className={cx(css.tableCell, css.disabled, {
          [css.flexStart]: !renderAmount,
        })}
      >
        {amountValue}
      </div>
    </div>
  )
}
